/***
*
*   ACCOUNTS
*   Manage all accounts signed up to your application
*
**********/

import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Table, Input, Space, Typography, Button, Tag, Row, Col,
  Statistic, Modal, message
} from 'antd';
import {
  BankOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  MailOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { fetchAccounts, invalidateAccountsCache } from '../store/slices/accountsSlice';
import { fetchPlans } from '../store/slices/plansSlice';
import { Animate, ViewContext, AuthContext } from 'components/lib';
import Axios from 'axios';

const { Title, Text } = Typography;
// const { Option } = Select;
const { confirm } = Modal;

export function Accounts() {
  const dispatch = useDispatch();
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);

  const [searchInput, setSearchInput] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const accountsState = useSelector((state) => state.accounts);
  const plansState = useSelector((state) => state.plans);
  const { data: accounts, loading } = accountsState;

  useEffect(() => {
    dispatch(fetchAccounts());
    dispatch(fetchPlans());
  }, [dispatch]);

  useEffect(() => {
    if (accounts) {
      let filtered = accounts;
      if (searchInput) {
        const search = searchInput.toLowerCase();
        filtered = accounts.filter(account =>
          account.name?.toLowerCase().includes(search) ||
          account.owner_email?.toLowerCase().includes(search) ||
          account.owner_name?.toLowerCase().includes(search)
        );
      }
      setFilteredData(filtered);
    }
  }, [accounts, searchInput]);

  const handleRefresh = () => {
    dispatch(invalidateAccountsCache());
    dispatch(fetchAccounts());
  };

  const handleEdit = (account) => {
    viewContext.modal.show({
      title: 'Edit Account',
      form: {
        id: {
          type: 'hidden',
          value: account.id
        },
        plan: {
          label: 'Plan',
          type: 'select',
          options: plansState.data?.list || [],
          default: account.plan,
          required: true,
        },
        active: {
          label: 'Status',
          type: 'select',
          default: account.active ? 1 : 0,
          options: [
            { value: 1, label: 'Active' },
            { value: 0, label: 'Cancelled' }
          ],
          required: true
        },
      },
      buttonText: 'Save',
      url: `${authContext.remote_server}/api/account/plan`,
      method: 'PATCH'
    }, () => {
      message.success(`${account.name} was updated`);
      handleRefresh();
    });
  };

  const handleDelete = (account) => {
    confirm({
      title: 'Delete Account',
      content: `Are you sure you want to close ${account.owner_email}'s account? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await Axios.delete(`${authContext.remote_server}/api/account/${account.id}`);
          message.success(`${account.owner_email} was deleted`);
          handleRefresh();
        } catch (err) {
          message.error('Failed to delete account');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Owner',
      key: 'owner',
      render: (_, record) => (
        <div>
          <Text>{record.owner_name || 'N/A'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.owner_email}</Text>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => <Tag color="blue">{plan || 'Free'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'} icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          {record.owner_email && (
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => window.location.href = `mailto:${record.owner_email}`}
            />
          )}
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      ),
    },
  ];

  const tableData = filteredData.map(account => ({
    ...account,
    key: account.id || account._id,
  }));

  const activeCount = accounts?.filter(a => a.active).length || 0;
  const inactiveCount = accounts?.filter(a => !a.active).length || 0;

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BankOutlined style={{ marginRight: 8 }} />
              Accounts
            </Title>
            <Text type="secondary">Manage all accounts signed up to your application</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Accounts"
                value={accounts?.length || 0}
                prefix={<BankOutlined style={{ color: '#7c3aed' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Active"
                value={activeCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Inactive"
                value={inactiveCount}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Search & Table */}
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search by name, owner, or email..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
              style={{ maxWidth: 400 }}
            />
          </div>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              pageSize: 25,
              showTotal: (total) => `Total ${total} accounts`,
            }}
          />
        </Card>
      </div>
    </Animate>
  );
}